import { Buildable } from "ts-essentials";
import {
    Contract,
    Repetition,
    ScheduleTimeType,
    ScheduleType,
    Unit,
    WorkingTimeClassicType,
} from "../../__generated__/graphql";
import { useCallback } from "react";
import { useI18n } from "../../hooks/i18n-hooks";
import { EnabledWorkingTimeSpecificType } from "./TimeSheetSpecificDatesComponent";
import { isOnDemandWorkingTime } from "../../utils";

export const ON_DEMAND_WORKING_TIME_CLASSIC_SPEC = {
    repetition: Repetition.OnDemand,
    unit: Unit.Hours,
    unitCount: null,
    dailyHours: null,
};

/**
 * Sets the contract's working time. On demand, nothing can be projected over a year, so the AVS
 * and LPP flags become the user's own choices: they are pre-filled (AVS on, LPP off) when the
 * contract becomes on demand, and reset when it stops being on demand, where they are overrides.
 */
export const setContractWorkingTimeClassicSpec = (
    contract: Buildable<Contract>,
    newValue: Buildable<WorkingTimeClassicType> | null | undefined,
): Buildable<Contract> => {
    const wasOnDemand = isOnDemandWorkingTime(contract.workingTimeClassicSpec);
    const isOnDemand = isOnDemandWorkingTime(newValue);
    return {
        ...contract,
        workingTimeClassicSpec: newValue,
        ...(isOnDemand !== wasOnDemand && {
            disableAvsSmallSalaryExemption: isOnDemand,
            forceLpp: false,
        }),
    };
};

export type WorkingTimeComponentProps = {
    disabled?: boolean;
    contract: Buildable<Contract>;
    setContract: (m: Buildable<Contract>) => void;
    // on demand is offered for hourly contracts only, and not under an LSE legal employer
    allowOnDemand?: boolean;
};

export const MIN_HOURS_PER_WEEK_FULL_TIME = 35;
export const MAX_HOURS_PER_WEEK_FULL_TIME = 51; // Highest values currently in all the CCTs that we support

const HOURS_INCREMENT_COARSE = 0.5;
const HOURS_INCREMENT_FINE = 0.25;
const HOURS_FINE_INCREMENT_THRESHOLD = 40;

export const generateHoursPerWeekSelection = (
    minHoursPerWeek: number,
    maxHoursPerWeek: number,
    defaultHoursPerWeek?: number,
): number[] => {
    const array: number[] = [];
    const clampedMax = Math.min(maxHoursPerWeek, MAX_HOURS_PER_WEEK_FULL_TIME);

    let i = minHoursPerWeek;
    while (i <= clampedMax) {
        const rounded = Math.round(i * 100) / 100;
        array.push(rounded);

        const increment =
            rounded >= HOURS_FINE_INCREMENT_THRESHOLD
                ? HOURS_INCREMENT_FINE
                : HOURS_INCREMENT_COARSE;

        if (
            defaultHoursPerWeek &&
            !array.includes(defaultHoursPerWeek) &&
            rounded + increment > defaultHoursPerWeek
        ) {
            array.push(defaultHoursPerWeek);
        }

        i = Math.round((i + increment) * 100) / 100;
    }

    return array;
};

export const MAX_PHYSICAL_NB_DAYS_PER_WEEK = 7;
export const MAX_PHYSICAL_DAILY_HOURS = 24;

export const MAX_NB_DAYS_PER_FIXED_MISSION = 23;

export const maxHoursPerWeekForRepetition = (
    repetition: Repetition,
    maxHoursPerWeek: number,
): number => {
    switch (repetition) {
        case Repetition.Week:
            return maxHoursPerWeek;
        case Repetition.Week2:
            return maxHoursPerWeek * 2;
        case Repetition.Week3:
            return maxHoursPerWeek * 3;
        case Repetition.Week4:
            return maxHoursPerWeek * 4;
        case Repetition.Month:
            return maxHoursPerWeek * 5;
        case Repetition.None: {
            const maxHoursPerDay = maxHoursPerWeek / 5;
            return Math.round(MAX_NB_DAYS_PER_FIXED_MISSION * maxHoursPerDay * 100) / 100; // round to 2 decimal places
        }
    }
    return maxHoursPerWeek;
};

export const maxDaysPerWeekForRepetition = (repetition: Repetition) => {
    // max days per week is checked only agains max number of days in a week.
    // then we will check hours per day so max hours per week of the CCT is respected
    const maxDaysPerWeek = MAX_PHYSICAL_NB_DAYS_PER_WEEK;
    switch (repetition) {
        case Repetition.Week:
            return MAX_PHYSICAL_NB_DAYS_PER_WEEK;
        case Repetition.Week2:
            return MAX_PHYSICAL_NB_DAYS_PER_WEEK * 2;
        case Repetition.Week3:
            return MAX_PHYSICAL_NB_DAYS_PER_WEEK * 3;
        case Repetition.Week4:
            return MAX_PHYSICAL_NB_DAYS_PER_WEEK * 4;
        case Repetition.Month:
            return MAX_PHYSICAL_NB_DAYS_PER_WEEK * 5;
        case Repetition.None:
            return MAX_NB_DAYS_PER_FIXED_MISSION;
    }
    return maxDaysPerWeek;
};

export type WorkingTimeClassicSpecErrorType = {
    errorRepetition: boolean | undefined | string;
    errorUnitCount: boolean | undefined | string;
    errorDailyHours: boolean | undefined | string;
    hasError: boolean;
};

export const useHasErrorWorkingTimeClassicSpec = () => {
    const i18n = useI18n();
    return useCallback(
        (
            unit: Unit,
            repetition: Repetition | undefined,
            unitCount: number | undefined,
            maxHoursPerWeek: number,
            dailyHours: number | undefined,
        ): WorkingTimeClassicSpecErrorType => {
            let errorRepetition = undefined;
            let errorUnitCount = undefined;
            let errorDailyHours = undefined;
            if (repetition === Repetition.OnDemand) {
                // no volume is guaranteed: there is no number of hours to check
                return {
                    errorRepetition: undefined,
                    errorUnitCount: undefined,
                    errorDailyHours: undefined,
                    hasError: false,
                };
            }
            if (!repetition) {
                errorRepetition = i18n.t("utils.working_time.classic.error.repetition_not_set");
            } else {
                if (!unitCount) {
                    errorUnitCount = i18n.t("utils.working_time.classic.error.unit_count_not_set");
                }
            }
            if (unit === Unit.Days && !dailyHours) {
                errorDailyHours = i18n.t("utils.working_time.classic.error.daily_hours_not_set");
            }
            if (repetition && unitCount) {
                if (unit === Unit.Hours) {
                    const max = maxHoursPerWeekForRepetition(repetition, maxHoursPerWeek);
                    if (unitCount > max) {
                        errorUnitCount = i18n.t(
                            `utils.working_time.classic.error.hours_unit_count_too_high`,
                            {
                                count: max,
                            },
                        );
                    }
                }
                if (unit === Unit.Days) {
                    const maxPhysicaldays = maxDaysPerWeekForRepetition(repetition);
                    if (unitCount > maxPhysicaldays) {
                        errorUnitCount = i18n.t(
                            `utils.working_time.classic.error.days_unit_count_too_high`,
                            {
                                count: maxPhysicaldays,
                            },
                        );
                    }
                }
            }
            if (unit === Unit.Days && dailyHours && unitCount && repetition) {
                // check if daily hours are too high according to max hours per week in the CCT
                const max = maxHoursPerWeekForRepetition(repetition, maxHoursPerWeek);
                const maxDailyHours = Math.min(
                    Math.round((max / unitCount) * 100) / 100,
                    MAX_PHYSICAL_DAILY_HOURS,
                ); // round to 2 decimal places
                if (dailyHours > maxDailyHours) {
                    errorDailyHours = i18n.t(
                        "utils.working_time.classic.error.daily_hours_too_high",
                        {
                            count: maxDailyHours,
                        },
                    );
                }
            }
            const hasError = !!errorRepetition || !!errorUnitCount || !!errorDailyHours;
            return {
                errorRepetition: errorRepetition,
                errorUnitCount: errorUnitCount,
                errorDailyHours: errorDailyHours,
                hasError,
            };
        },
        [i18n],
    );
};

/**
 * Whether every value the spec needs is set. An on-demand spec guarantees no volume, so it needs
 * no number of hours.
 */
export const isWorkingTimeClassicSpecFilled = (
    spec?: Buildable<WorkingTimeClassicType> | null,
): boolean => {
    if (isOnDemandWorkingTime(spec)) {
        return true;
    }
    const dailyIsValid = spec?.unit !== Unit.Days || !!spec?.dailyHours;
    return !!(
        spec?.repetition &&
        spec?.unit &&
        spec?.unitCount &&
        spec.unitCount >= 0 &&
        dailyIsValid
    );
};

export const STANDARD_WORKING_TIME_CLASSIC_SPEC = {
    repetition: Repetition.Week,
    unitCount: 42,
    unit: Unit.Hours,
};

export type WorkingTimeSpecificType = {
    errors: { [key: string]: boolean };
    timeErrors: Array<ScheduleTypeErrorInfo | null>;
    hasError: boolean;
};

export type ScheduleTypeErrorInfo = {
    errors: { [key: string]: boolean };
    indexErrors: Array<string[]>;
    hasError: boolean;
};

export const checkScheduleType = (
    dates: EnabledWorkingTimeSpecificType[],
    vacationOverflow?: number,
): WorkingTimeSpecificType => {
    const result = {
        errors: {} as { [key: string]: boolean },
        indexErrors: Array.from({ length: dates.length }, () => [] as string[]),
        timeErrors: Array.from(
            { length: dates.length },
            () => null as ScheduleTypeErrorInfo | null,
        ),
        hasError: false,
    };
    const rDates = dates || [];

    // check if slot is smaller than min slot duration
    rDates.forEach((x, idx) => {
        if (x.enabled) {
            const ret = checkScheduleTypeList(x?.daySchedule?.fixedTimes, vacationOverflow);
            if (ret.hasError) {
                result.errors["workingTime.invalidTime"] = true;
            }
            result.timeErrors[idx] = ret;
        }
    });
    result.hasError = Object.keys(result.errors).length > 0;
    return result;
};

export const checkScheduleTypeList = (
    times?: Array<Buildable<ScheduleType> | undefined> | null,
    vacationOverflow?: number,
): ScheduleTypeErrorInfo => {
    const result = {
        errors: {} as { [key: string]: boolean },
        // map times and return an array of empty arrays
        indexErrors: Array.from({ length: times?.length || 0 }, () => [] as string[]),
        hasError: false,
    };
    const rTimes = times || [];

    // check if slot is smaller than min slot duration
    rTimes.forEach((x, idx) => {
        const { startTime = 0, endTime = 0 } = x || {};
        if (endTime - startTime < 1) {
            result.errors["workingTime.invalidTime"] = true;
            result.indexErrors[idx] = [
                ...(result.indexErrors[idx] || []),
                "workingTime.tooLowDuration",
            ];
        }
    });

    // check if there is any overlap
    rTimes.forEach((x, idx) => {
        if (vacationOverflow && vacationOverflow > 0 && x && x.type === ScheduleTimeType.Vacation) {
            result.errors["workingTime.insufficient_vacation"] = true;
            result.indexErrors[idx] = [
                ...(result.indexErrors[idx] || []),
                "workingTime.insufficient_vacation",
            ];
        }
        rTimes.forEach((y, idy) => {
            if (idx !== idy) {
                if (
                    x &&
                    y &&
                    (x.startTime || 0) < (y.endTime || 0) &&
                    (x.endTime || 0) > (y.startTime || 0)
                ) {
                    result.errors["workingTime.overlapError"] = true;
                    result.indexErrors[idx] = [
                        ...new Set([
                            ...(result.indexErrors[idx] || []),
                            "workingTime.overlapError",
                        ]),
                    ];
                }
            }
        });
    });
    result.hasError = Object.keys(result.errors).length > 0;
    return result;
};
